import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import { calcAgeFromDob, todayIsoDateLocal } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaWizardPath, ExtraditionMlaWizardShell, FieldError, inpClass } from './ExtraditionMlaWizardShell';
import { requiresCustodialAuthority } from './extraditionMlaHelpers';
import { useExtraditionMlaLookups } from './useExtraditionMlaLookups';
import { validateExtraditionMlaStep } from './extraditionMlaStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 255;
const MAX_DESC = 2000;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string; code?: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string; code?: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function ExtraditionMlaStep2AccusedPersonPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = useExtraditionMlaLookups('step2');

  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const age = useMemo(() => calcAgeFromDob(draft.accusedDob), [draft.accusedDob]);
  const custodyStatusOptions = useMemo(
    () =>
      mergeLookupItems(lookups.custodyStatuses, draft.custodyStatusId, draft.custodyStatusName),
    [draft.custodyStatusId, draft.custodyStatusName, lookups.custodyStatuses],
  );
  const selectedCustodyStatus = useMemo(
    () => custodyStatusOptions.find((s) => s.id === draft.custodyStatusId),
    [custodyStatusOptions, draft.custodyStatusId],
  );
  const showCustodialAuthority = requiresCustodialAuthority(
    draft.custodyStatusName,
    selectedCustodyStatus?.code,
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateExtraditionMlaStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(extraditionMlaWizardPath(3, draft.draftRequestId));
  };

  return (
    <ExtraditionMlaWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionAccusedPersonDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.accusedName} <span className="req">*</span>
          </label>
          <input
            id="emla-accusedName"
            className={inpClass(Boolean(errors.accusedName))}
            type="text"
            value={draft.accusedName}
            onChange={(e) => {
              setDraft((d: ExtraditionMlaWizardDraft) => ({ ...d, accusedName: maxLen('accusedName', e.target.value, MAX_NAME) }));
            }}
            placeholder={f.enterAccusedName}
          />
          <FieldError message={errors.accusedName} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.nationality} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-nationality"
              hasError={Boolean(errors.nationalityId)}
              value={draft.nationalityId}
              disabled={lookups.loading}
              placeholder={lookups.loading ? f.loadingNationalities : f.selectNationality}
              options={toOpts(lookups.nationalities.length ? lookups.nationalities : lookups.countries)}
              onChange={(id) => {
                const items = lookups.nationalities.length ? lookups.nationalities : lookups.countries;
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  nationalityId: id,
                  nationalityName: lookupName(items, id) }));
                clearErr('nationalityId');
              }}
            />
            <FieldError message={errors.nationalityId} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.countryOfOrigin} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-countryOfOrigin"
              hasError={Boolean(errors.countryOfOriginId)}
              value={draft.countryOfOriginId}
              disabled={lookups.loading}
              placeholder={lookups.loading ? f.loadingCountries : f.selectCountryOfOrigin}
              options={toOpts(lookups.countries)}
              onChange={(id) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  countryOfOriginId: id,
                  countryOfOriginName: lookupName(lookups.countries, id) }));
                clearErr('countryOfOriginId');
              }}
            />
            <FieldError message={errors.countryOfOriginId} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.dateOfBirth} <span className="req">*</span>
            </label>
            <FormattedDateInput
              id="emla-accusedDob"
              className={inpClass(Boolean(errors.accusedDob))}
              value={draft.accusedDob}
              maxDate={maxDob}
              enableYmdViews
              onChange={(next) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({ ...d, accusedDob: next }));
                clearErr('accusedDob');
              }}
            />
            <FieldError message={errors.accusedDob} />
          </div>
          <div className="ccr-fg">
            <label>{f.age}</label>
            <div className="ccr-inp ccr-inp--static" aria-live="polite">
              {typeof age === 'number' ? String(age) : '—'}
            </div>
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.gender} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-accusedGender"
              hasError={Boolean(errors.accusedGenderId)}
              value={draft.accusedGenderId}
              disabled={lookups.loading}
              placeholder={f.selectGender}
              options={toOpts(lookups.genders)}
              onChange={(id) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  accusedGenderId: id,
                  accusedGenderName: lookupName(lookups.genders, id) }));
                clearErr('accusedGenderId');
              }}
            />
            <FieldError message={errors.accusedGenderId} />
          </div>
          <div className="ccr-fg">
            <label>{f.passportIdNumber}</label>
            <input
              id="emla-passport"
              className={inpClass(Boolean(errors.passportOrIdNumber))}
              type="text"
              value={draft.passportOrIdNumber}
              onChange={(e) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  passportOrIdNumber: maxLen('passportOrIdNumber', e.target.value, MAX_NAME) }));
              }}
            />
            <FieldError message={errors.passportOrIdNumber} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.currentCustodyStatus} <span className="req">*</span>
            </label>
            <WizardSelect
              id="emla-custodyStatus"
              hasError={Boolean(errors.custodyStatusId)}
              value={draft.custodyStatusId}
              disabled={lookups.loading}
              placeholder={lookups.loading ? f.loadingCustodyStatuses : f.selectCustodyStatus}
              options={toOpts(custodyStatusOptions)}
              onChange={(id) => {
                const item = custodyStatusOptions.find((x) => x.id === id);
                const name = item?.name ?? lookupName(custodyStatusOptions, id);
                const code = item?.code ?? '';
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  custodyStatusId: id,
                  custodyStatusName: name,
                  ...(!requiresCustodialAuthority(name, code) ? { custodialAuthority: '' } : {}) }));
                clearErr('custodyStatusId');
              }}
            />
            <FieldError message={errors.custodyStatusId} />
          </div>
          {showCustodialAuthority ? (
            <div className="ccr-fg">
              <label>
                {f.custodialAuthority} <span className="req">*</span>
              </label>
              <input
                id="emla-custodialAuthority"
                className={inpClass(Boolean(errors.custodialAuthority))}
                type="text"
                value={draft.custodialAuthority}
                onChange={(e) => {
                  setDraft((d: ExtraditionMlaWizardDraft) => ({
                    ...d,
                    custodialAuthority: maxLen('custodialAuthority', e.target.value, MAX_NAME) }));
                }}
                placeholder={f.custodialAuthorityHint}
              />
              <FieldError message={errors.custodialAuthority} />
            </div>
          ) : null}
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.lastKnownLocation}</label>
          <textarea
            id="emla-lastKnownLocation"
            className={inpClass(Boolean(errors.lastKnownLocation))}
            rows={3}
            value={draft.lastKnownLocation}
            onChange={(e) => {
              setDraft((d: ExtraditionMlaWizardDraft) => ({
                ...d,
                lastKnownLocation: maxLen('lastKnownLocation', e.target.value, MAX_DESC) }));
            }}
          />
          <FieldError message={errors.lastKnownLocation} />
        </div>
      </SectionCard>

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(extraditionMlaWizardPath(1, draft.draftRequestId))}
        onNext={next}
        validateBeforeSave={validate}
      />
    </ExtraditionMlaWizardShell>
  );
}
